import React, { useState, useEffect } from "react";
import { Button, Col, Container, Row, Input, ButtonGroup } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  DynamicTable,
  useTableHook,
  DynamicTableHelper,
} from "@workspace/common";
import {
  INTERVIEW_MANDATORY_OPTIONS,
  INTERVIEW_INITIAL_OPTIONS,
} from "../../pages/jobInterviewCanvasConstants";
import TableItemDisplay from "@workspace/common/src/Components/DynamicTable/TableItemDisplay";
import TableRowsPerPageWithNav from "@workspace/common/src/Components/DynamicTable/TableRowsPerPageWithNav";
import { render } from "react-dom";
import { fetchJobLists } from "@workspace/job/src/store/actions";
import { fetchJobTimelineList } from "@workspace/job/src/store/actions";
import { fetchJobCandidates } from "../../store/actions";

const InterviewCandidateCanvas = ({
  setCandidateId,
  setActiveStep,
  setJobTimeLineData,
  onPageSizeChange,
  setActiveOffCanvas,
  setOriginalOrder,
  getMaxOrder,
  setReadOnly,
  ...props
}) => {
  const dispatch = useDispatch();
  const jobsData = useSelector(
    (state) => state.InterviewFormReducer?.jobTimeline
  );
  const jobCandidateData = useSelector(
    (state) => state.InterviewFormReducer?.jobCandidates
  );
  const data = jobsData?.data?.jobTimelineEntities;
  const [tableConfig, setTableConfig] = useState([]);
  const [gridView, setGridView] = useState("submissions");
  const [jobFilterBubble, setJobFilterBubble] = useState(null);

  const handleNewInterview = () => {
    setReadOnly(false);
    setActiveOffCanvas("new_Interview");
    toggleOffcanvas();
  };

  const customRenderList = [
    {
      names: ["Job Type"],
      render: (data, opt) => {
        let badgeStyle = { backgroundColor: "dark" };

        return (
          <span className="badge" style={badgeStyle}>
            {data.status}
          </span>
        );
      },
    },
    {
      names: ["Job Title"],
      render: (data, opt) => {
        return (
          <span style={{ color: "blue", fontWeight: 400 }}>
            {data.candidate.candidateSubmissionData.firstName}
          </span>
        );
      },
    },
  ];

  //Table Hook
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    handleRowCheck,
    selectAllRows,
    activeRow,
    setActiveRow,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
    },
    INTERVIEW_MANDATORY_OPTIONS,
    INTERVIEW_INITIAL_OPTIONS,
    customRenderList
  );

  const getOrder = (data) => {
    const order = getMaxOrder(data);
    if (order >= 10 && order <= 12) {
      setOriginalOrder(order);
    } else if (order < 10) {
      setOriginalOrder(10);
    } else if (order > 12) {
      setOriginalOrder(12);
    }
  };

  const generateJobCanvasConfig = (customConfig) => {
    return [
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {jobsData?.data?.page * jobsData?.data?.pageSize + (index + 1)}.
          </div>
        ),
      },

      ...customConfig,

      {
        header: "Action",
        name: "action",
        sort: false,
        render: (data) => (
          <div className="d-flex column-gap-2 justify-content-center">
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                setJobTimeLineData(data);
                setActiveOffCanvas("new_Interview");
                setActiveStep(13);
                setCandidateId(data?.candidate?.id);
                // getOrder(data);
                setOriginalOrder(getMaxOrder(data));
                setReadOnly(false);
              }}
            >
              Select
            </Button>
          </div>
        ),
      },
    ];
  };
  // Set default view on component mount
  useEffect(() => {
    setCustomConfigData(INTERVIEW_MANDATORY_OPTIONS);
  }, []);

  // Update Interview Form Data on jobCandidateData changes
  useEffect(() => {
    setJobTimeLineData(jobCandidateData);
  }, [jobCandidateData]);

  // Fetch the job list data on initial render (removing condition for searchFields)
  useEffect(() => {
    setActiveRow([]);
    const payload = {
      page: pageRequest?.page,
      pageSize: pageRequest?.pageSize,
      sortBy: pageRequest?.sortBy,
      sortDirection: pageRequest?.sortDirection,
      searchTerm: search,
      isDowload: false,
      filters: [],
      externalFilters: [],
      searchFields: pageRequest?.searchFields,
      sortType: null,
    };
    if (pageRequest.searchFields?.length > 0) {
      dispatch(fetchJobCandidates(payload));
    }
  }, [pageRequest, gridView, dispatch]);

  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateJobCanvasConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig?.find(
          (oldItem) => oldItem?.name === item?.name
        );
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateJobCanvasConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  // Update the page info when job Data changes
  useEffect(() => {
    if (jobsData) {
      const pageData = {
        page: jobsData?.data?.page,
        totalPages: jobsData?.data?.totalPages,
        totalElements: jobsData?.data?.totalElements,
        pageSize: jobsData?.data?.pageSize,
      };
      setPageInfoData(pageData);
    }
    setTableData(data);
  }, [jobsData]);

  return (
    <React.Fragment>
      <div>
        <Container fluid>
          <Row>
            <Col lg={12}>
              <div className="listjs-table d-flex flex-column">
                <Row className="mb-2 pt-4 ps-2 pe-2 d-flex align-items-center">
                  <Col xs="4">
                    <div className="d-flex gap-2 align-items-center">
                      <h4
                        style={{
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span>{`Schedule Interviews (${
                          pageInfo?.totalElements ?? 0
                        })`}</span>
                      </h4>
                      <div className="search-box">
                        <form onSubmit={pageRequestSet?.setSearchTerm}>
                          <Input
                            type="text"
                            placeholder="Search"
                            className="form-control search"
                            value={search}
                            style={{
                              width: "250px",
                              height: "40px",
                              borderColor: "#d3d3d3",
                              borderRadius: "20px",
                            }}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </form>
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="d-flex column-gap  gap-1 justify-content-end align-items-center">
                      <TableItemDisplay pageInfo={pageInfo} />
                      <div
                        style={{
                          width: "2px",
                          height: "20px",
                          backgroundColor: "#adb5bd",
                          marginLeft: "12px",
                        }}
                      ></div>
                      <TableRowsPerPageWithNav
                        pageInfo={pageInfo}
                        pageRequestSet={pageRequestSet}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <Row>
            <div className="dynamic-table-wrapper">
              <DynamicTable
                config={tableConfig}
                data={data ?? []}
                pageRequestSet={pageRequestSet}
                pageInfo={pageInfo}
                isLoading={false}
                freezeHeader={true}
                activeRow={activeRow}
                setTableConfig={setTableConfig}
                pageRequest={pageRequest}
                tableHeight="100%"
              />
            </div>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default InterviewCandidateCanvas;
